import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft } from "lucide-react";

const EnglishLevelTest = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [testStarted, setTestStarted] = useState(false);

  const questions = [
    // A1 Grammar Questions
    {
      level: "A1",
      question: "My name ____ Sarah.",
      options: [
        { id: "a", text: "am", correct: false },
        { id: "b", text: "is", correct: true },
        { id: "c", text: "are", correct: false },
        { id: "d", text: "be", correct: false }
      ],
      explanation: "Verb 'to be' - 3rd person singular: name is"
    },
    {
      level: "A1",
      question: "She ____ two cats and a dog.",
      options: [
        { id: "a", text: "have", correct: false },
        { id: "b", text: "has", correct: true },
        { id: "c", text: "having", correct: false },
        { id: "d", text: "had", correct: false }
      ],
      explanation: "Verb 'to have' - 3rd person singular present: has"
    },
    {
      level: "A1",
      question: "They ____ to school every day.",
      options: [
        { id: "a", text: "goes", correct: false },
        { id: "b", text: "going", correct: false },
        { id: "c", text: "go", correct: true },
        { id: "d", text: "went", correct: false }
      ],
      explanation: "Present simple - 3rd person plural: they go"
    },
    {
      level: "A1",
      passage: "Hello! My name is John. I live in London. I am 25 years old and I work as a teacher at a primary school.",
      question: "Where does John live?",
      options: [
        { id: "a", text: "In Paris", correct: false },
        { id: "b", text: "In London", correct: true },
        { id: "c", text: "In New York", correct: false },
        { id: "d", text: "In Berlin", correct: false }
      ],
      explanation: "John says 'I live in London'"
    },
    {
      level: "A1",
      passage: "Emma loves reading books. She reads every evening before she goes to bed. Her favorite books are mystery novels.",
      question: "When does Emma read?",
      options: [
        { id: "a", text: "In the morning", correct: false },
        { id: "b", text: "In the evening", correct: true },
        { id: "c", text: "At noon", correct: false },
        { id: "d", text: "In the afternoon", correct: false }
      ],
      explanation: "'She reads every evening' means she reads in the evening"
    },
    // A2 Questions
    {
      level: "A2",
      question: "Yesterday, I ____ to the cinema with my friends.",
      options: [
        { id: "a", text: "go", correct: false },
        { id: "b", text: "went", correct: true },
        { id: "c", text: "going", correct: false },
        { id: "d", text: "will go", correct: false }
      ],
      explanation: "Past simple: went is the past tense of go"
    },
    {
      level: "A2",
      question: "She ____ her homework when the phone rang.",
      options: [
        { id: "a", text: "does", correct: false },
        { id: "b", text: "did", correct: false },
        { id: "c", text: "was doing", correct: true },
        { id: "d", text: "will do", correct: false }
      ],
      explanation: "Past continuous for ongoing action: was doing"
    },
    {
      level: "A2",
      question: "I need ____ milk for the recipe.",
      options: [
        { id: "a", text: "a", correct: false },
        { id: "b", text: "some", correct: true },
        { id: "c", text: "an", correct: false },
        { id: "d", text: "any", correct: false }
      ],
      explanation: "'Some' is used with uncountable nouns in positive sentences"
    },
    {
      level: "A2",
      passage: "Last weekend, Maria went to the market. She bought fresh vegetables, fruits, and bread. Then she went home to prepare lunch for her family.",
      question: "What did Maria do after the market?",
      options: [
        { id: "a", text: "She went to the cinema", correct: false },
        { id: "b", text: "She went home", correct: true },
        { id: "c", text: "She went to work", correct: false },
        { id: "d", text: "She called a friend", correct: false }
      ],
      explanation: "'Then she went home' indicates what she did after the market"
    },
    {
      level: "A2",
      passage: "David enjoys traveling. He has already visited Italy, Spain, and Germany. Next year, he wants to go to Japan.",
      question: "Where does David want to go next year?",
      options: [
        { id: "a", text: "To Italy", correct: false },
        { id: "b", text: "To Spain", correct: false },
        { id: "c", text: "To Japan", correct: true },
        { id: "d", text: "To Germany", correct: false }
      ],
      explanation: "'Next year, he wants to go to Japan' clearly states his plan"
    },
    // B1 Questions
    {
      level: "B1",
      question: "If I had more money, I ____ a new car.",
      options: [
        { id: "a", text: "buy", correct: false },
        { id: "b", text: "will buy", correct: false },
        { id: "c", text: "would buy", correct: true },
        { id: "d", text: "bought", correct: false }
      ],
      explanation: "Second conditional: if + past simple, would + infinitive"
    },
    {
      level: "B1",
      question: "The report must ____ by Friday.",
      options: [
        { id: "a", text: "finish", correct: false },
        { id: "b", text: "be finished", correct: true },
        { id: "c", text: "finishing", correct: false },
        { id: "d", text: "finished", correct: false }
      ],
      explanation: "Passive voice with modal: must be + past participle"
    },
    {
      level: "B1",
      question: "I wish I ____ speak Spanish fluently.",
      options: [
        { id: "a", text: "can", correct: false },
        { id: "b", text: "could", correct: true },
        { id: "c", text: "will", correct: false },
        { id: "d", text: "would", correct: false }
      ],
      explanation: "'Wish' is followed by past simple or 'could' for present unreal situations"
    },
    {
      level: "B1",
      passage: "Remote work offers both advantages and disadvantages. On one hand, it saves commuting time and provides more flexibility. On the other hand, some employees feel isolated and struggle to separate work life from personal life.",
      question: "According to the text, what is an advantage of remote work?",
      options: [
        { id: "a", text: "It saves commuting time", correct: true },
        { id: "b", text: "It reduces isolation", correct: false },
        { id: "c", text: "It makes work easier", correct: false },
        { id: "d", text: "It requires less effort", correct: false }
      ],
      explanation: "'It saves commuting time' is clearly mentioned as an advantage"
    },
    {
      level: "B1",
      passage: "To effectively learn a foreign language, memorizing vocabulary is not enough. It is essential to practice regularly, immerse yourself in the culture, and dare to speak even if you make mistakes.",
      question: "According to the text, what should you do to learn a language?",
      options: [
        { id: "a", text: "Only memorize words", correct: false },
        { id: "b", text: "Practice and immerse in the culture", correct: true },
        { id: "c", text: "Never make mistakes", correct: false },
        { id: "d", text: "Avoid speaking", correct: false }
      ],
      explanation: "The text emphasizes practice, immersion, and speaking despite mistakes"
    },
    // B2 Questions
    {
      level: "B2",
      question: "Although it ____ raining, we decided to go out.",
      options: [
        { id: "a", text: "is", correct: false },
        { id: "b", text: "was", correct: true },
        { id: "c", text: "will be", correct: false },
        { id: "d", text: "has been", correct: false }
      ],
      explanation: "'Although' introduces a concessive clause; past simple for completed action"
    },
    {
      level: "B2",
      question: "Not only ____ intelligent, but she is also very kind.",
      options: [
        { id: "a", text: "she is", correct: false },
        { id: "b", text: "is she", correct: true },
        { id: "c", text: "she was", correct: false },
        { id: "d", text: "was she", correct: false }
      ],
      explanation: "Inversion after 'not only' at the start of a sentence"
    },
    {
      level: "B2",
      question: "Had I known about the meeting, I ____ attended.",
      options: [
        { id: "a", text: "will have", correct: false },
        { id: "b", text: "would have", correct: true },
        { id: "c", text: "would", correct: false },
        { id: "d", text: "will", correct: false }
      ],
      explanation: "Third conditional with inverted 'had': would have + past participle"
    },
    {
      level: "B2",
      passage: "Climate change represents one of the major challenges of our time. Although scientists are multiplying warnings, the measures taken remain insufficient. The energy transition requires not only massive investments but also a profound change in individual and collective mindsets and behaviors.",
      question: "According to the text, what is needed for the energy transition?",
      options: [
        { id: "a", text: "Only money", correct: false },
        { id: "b", text: "Investments and a change in mindsets", correct: true },
        { id: "c", text: "Ignoring scientists", correct: false },
        { id: "d", text: "Waiting for it to pass", correct: false }
      ],
      explanation: "The text mentions both 'massive investments' and 'change in mindsets'"
    },
    {
      level: "B2",
      passage: "Artificial intelligence raises as many hopes as concerns. While it promises significant advances in fields such as medicine and education, it also raises fundamental ethical questions about privacy, employment, and human autonomy. A democratic debate is essential to regulate its development.",
      question: "Why does artificial intelligence need a democratic debate?",
      options: [
        { id: "a", text: "It is too expensive", correct: false },
        { id: "b", text: "It raises important ethical questions", correct: true },
        { id: "c", text: "It doesn't work well", correct: false },
        { id: "d", text: "Nobody uses it", correct: false }
      ],
      explanation: "The text mentions 'fundamental ethical questions' as the reason for debate"
    }
  ];

  const handleAnswer = (questionIndex, optionId) => {
    setAnswers({ ...answers, [questionIndex]: optionId });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const calculateResults = () => {
    let a1Score = 0;
    let a2Score = 0;
    let b1Score = 0;
    let b2Score = 0;

    questions.forEach((question, index) => {
      const selectedOption = answers[index];
      const correctOption = question.options.find(opt => opt.correct);
      
      if (selectedOption === correctOption.id) {
        switch (question.level) {
          case "A1":
            a1Score++;
            break;
          case "A2":
            a2Score++;
            break;
          case "B1":
            b1Score++;
            break;
          case "B2":
            b2Score++;
            break;
          default:
            break;
        }
      }
    });

    return { a1Score, a2Score, b1Score, b2Score };
  };

  const determineLevel = () => {
    const scores = calculateResults();
    const total = scores.a1Score + scores.a2Score + scores.b1Score + scores.b2Score;
    const percentage = (total / questions.length) * 100;

    if (scores.b2Score >= 3 && scores.b1Score >= 3 && percentage >= 75) {
      return { level: "B2", description: "Upper Intermediate - You have a good command of complex grammar structures" };
    } else if (scores.b1Score >= 3 && scores.a2Score >= 3 && percentage >= 60) {
      return { level: "B1", description: "Intermediate - You can handle most grammar structures with confidence" };
    } else if (scores.a2Score >= 3 && scores.a1Score >= 3 && percentage >= 45) {
      return { level: "A2", description: "Elementary - You can use basic grammar structures with confidence" };
    } else {
      return { level: "A1", description: "Beginner - You are starting to build the foundations of the language" };
    }
  };

  const currentQ = questions[currentQuestion];

  if (!testStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
        <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Button
              variant="ghost"
              className="text-white hover:text-white/80 mb-4"
              onClick={() => navigate("/")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="test-title">
              English Level Test
            </h1>
            <p className="text-xl text-white/90">Assess your level from A1 to B2</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="shadow-2xl">
            <CardContent className="p-8 sm:p-12">
              <h2 className="text-3xl font-bold text-[#04539F] mb-6">How much English do you know?</h2>
              <div className="space-y-4 text-gray-700 mb-8">
                <p className="text-lg">Complete our free level test and discover your level.</p>
                <div className="flex gap-2 justify-center my-6">
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">A1</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">A2</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">B1</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">B2</span>
                </div>
                <div className="bg-[#04539F]/10 rounded-lg p-6 space-y-2">
                  <ul className="list-disc list-inside space-y-2">
                    <li>The test will take 10 to 15 minutes</li>
                    <li>Select the correct answer for each question</li>
                    <li>Take your time - there's no time limit</li>
                    <li>Discover your level after completing the test</li>
                  </ul>
                </div>
              </div>
              <Button
                size="lg"
                className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full py-6 text-lg"
                onClick={() => setTestStarted(true)}
                data-testid="start-test-btn"
              >
                Start Level Test
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showResults) {
    const result = determineLevel();
    const scores = calculateResults();
    const totalCorrect = scores.a1Score + scores.a2Score + scores.b1Score + scores.b2Score;

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
        <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-4xl font-bold text-white">Level Test Completed!</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="shadow-2xl mb-8">
            <CardContent className="p-8 sm:p-12">
              <div className="text-center mb-8">
                <div className="inline-block bg-[#04539F] text-white rounded-full px-12 py-6 mb-4">
                  <span className="text-6xl font-bold">{result.level}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{result.description}</h2>
                <p className="text-xl text-gray-600">You answered {totalCorrect} out of {questions.length} questions correctly</p>
              </div>

              <div className="bg-[#04539F]/10 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-[#04539F] mb-4">Your Answers</h3>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {questions.map((q, idx) => {
                    const userAnswer = answers[idx];
                    const correctAnswer = q.options.find(opt => opt.correct);
                    const isCorrect = userAnswer === correctAnswer.id;
                    
                    return (
                      <div key={idx} className="bg-white rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                            {isCorrect ? '✓' : '✗'}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 mb-2">{q.question}</p>
                            {!isCorrect && (
                              <p className="text-sm text-gray-600">
                                <span className="font-semibold">Correct answer:</span> {correctAnswer.text}
                                <br />
                                <span className="text-gray-500 italic">{q.explanation}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCurrentQuestion(0);
                    setAnswers({});
                    setShowResults(false);
                    setTestStarted(false);
                  }}
                  className="rounded-full"
                >
                  Retake Test
                </Button>
                <Button
                  className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                  onClick={() => navigate("/contact")}
                >
                  Contact Berni
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-white">English Level Test</h1>
            <div className="text-white text-sm">
              Question {currentQuestion + 1} of {questions.length}
            </div>
          </div>
          <div className="mt-4 bg-white/20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <div className="mb-6">
              {currentQ.passage && (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <p className="text-gray-800 leading-relaxed italic">{currentQ.passage}</p>
                </div>
              )}
              <h3 className="text-2xl font-bold text-[#04539F] mb-6">{currentQ.question}</h3>
            </div>

            <div className="space-y-3 mb-8">
              {currentQ.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleAnswer(currentQuestion, option.id)}
                  className={`w-full text-left p-5 rounded-lg border-2 transition-all text-lg ${
                    answers[currentQuestion] === option.id
                      ? 'border-[#04539F] bg-[#04539F]/10'
                      : 'border-gray-200 hover:border-[#04539F]/50'
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <div className="flex items-center">
                    <div className={`w-7 h-7 rounded-full border-2 mr-4 flex items-center justify-center ${
                      answers[currentQuestion] === option.id
                        ? 'border-[#04539F] bg-[#04539F] text-white'
                        : 'border-gray-300'
                    }`}>
                      {answers[currentQuestion] === option.id && '✓'}
                    </div>
                    <span className="font-medium text-gray-800">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleNext}
                disabled={!answers[currentQuestion]}
                className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full px-8"
                data-testid="next-btn"
              >
                {currentQuestion === questions.length - 1 ? 'Finish Test' : 'Next'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnglishLevelTest;
